import { Link } from 'react-router-dom';
import { TableTooltip } from './TableTooltip';
import { StyledLinkableCell, LinkIcon } from './cellStyles';
import type { LinkableCellProps } from '../components/types';

/**
 * LinkableCell - A cell component that displays a value with optional link icon
 *
 * Features:
 * - Click to navigate to related entity
 * - Optional link icon indicator
 * - Tooltip on hover
 * - Text overflow ellipsis
 *
 * @example
 * ```tsx
 * <LinkableCell
 *   value={row.styleName}
 *   url={`/style/${row.styleId}`}
 *   title="View style details"
 *   showLinkIcon
 *   isLinked={!!row.styleId}
 * />
 * ```
 */
export const LinkableCell = ({
  value = '',
  url,
  title,
  showLinkIcon = false,
  isLinked = true,
  newTab = false,
  isBold = false,
}: LinkableCellProps) => {
  if (!value) {
    return <span />;
  }

  const content = (
    <StyledLinkableCell>
      {showLinkIcon && isLinked && <LinkIcon data-testid="link-icon" size={16} />}
      <span style={isBold ? { fontWeight: 700 } : undefined}>{value?.toString()}</span>
    </StyledLinkableCell>
  );

  const linkProps = newTab ? { target: '_blank' as const, rel: 'noopener noreferrer' } : {};
  const isExternal = typeof url === 'string' && /^https?:\/\//i.test(url);
  const stopEditTrigger = (e: React.MouseEvent) => e.stopPropagation();

  const link = isExternal ? (
    <a href={url} {...linkProps} onClick={stopEditTrigger}>
      {content}
    </a>
  ) : (
    <Link to={url} {...linkProps} onClick={stopEditTrigger}>
      {content}
    </Link>
  );

  if (title) {
    return <TableTooltip title={title}>{link}</TableTooltip>;
  }

  return link;
};
